// ModelSelector.js
import React from 'react';
import {
  Button,
  Modal,
  Search,
  Toggle,
  Tooltip,
} from '@carbon/react';
import {
  ChevronDown,
  Checkmark,
  Information,
} from '@carbon/react/icons';
import { modelInfo } from './ModelInfo';

/* ────────────────────────────────────────── */
/* Utility: detect reduced viewport width    */
/* ────────────────────────────────────────── */
function useIsSmallScreen(breakpoint = 600) {
  const [isSmallScreen, setIsSmallScreen] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < breakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isSmallScreen;
}

/* ────────────────────────────────────────── */
/* Card representing one model               */
/* ────────────────────────────────────────── */
const ModelCard = ({
  modelId,
  selected,
  onSelect,
  showTechnicalSpecs,
  maxItems,
}) => {
  const model = modelInfo[modelId] || {
    name: modelId,
    description: 'AI model',
    strengths: [],
    limitations: [],
    parameters: {},
  };

  const parameters = model.parameters || {};
  const modalities = parameters.modalities || [];
  const strengths = maxItems ? model.strengths.slice(0, maxItems) : model.strengths;
  const limitations = maxItems ? model.limitations.slice(0, maxItems) : model.limitations;

  return (
    <div
      className={`model-card ${selected ? 'selected' : ''}`}
      onClick={() => onSelect(modelId)}
      role="button"
      tabIndex={0}
      aria-label={`Select model ${model.name}`}
    >
      {/* header row */}
      <div className="model-card-header">
        <h4>{model.name}</h4>

        {selected && <Checkmark size={16} className="active-check" />}

        <Tooltip label="Model documentation" align="left-bottom">
          <Button
            kind="ghost"
            size="sm"
            href={model.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Learn more about ${model.name}`}
          >
            <Information />
          </Button>
        </Tooltip>
      </div>

      <p className="model-description">{model.description}</p>

      {/* strengths & limits */}
      <div className="model-attributes">
        <div className="model-strengths">
          <h5>Strengths</h5>
          {strengths.map((s, i) => (
            <span key={`str-${i}`} className="model-tag strength">
              {s}
            </span>
          ))}
        </div>

        <div className="model-limitations">
          <h5>Limitations</h5>
          {limitations.map((l, i) => (
            <span key={`lim-${i}`} className="model-tag limitation">
              {l}
            </span>
          ))}
        </div>
      </div>

      {/* optional technical specs */}
      {showTechnicalSpecs && (
        <div className="model-parameters">
          <h5 className="parameters-heading">Technical Specifications</h5>
          <div className="parameter-grid">
            {parameters.contextWindow && (
              <div className="parameter-item">
                <span className="parameter-label">Context Window</span>
                <span className="parameter-value">
                  {parameters.contextWindow.toLocaleString()} tokens
                </span>
              </div>
            )}
            {parameters.maxTokens && (
              <div className="parameter-item">
                <span className="parameter-label">Max Output</span>
                <span className="parameter-value">
                  {parameters.maxTokens.toLocaleString()} tokens
                </span>
              </div>
            )}
            {parameters.modelSize && (
              <div className="parameter-item">
                <span className="parameter-label">Model Size</span>
                <span className="parameter-value">{parameters.modelSize}</span>
              </div>
            )}
            {parameters.trainingData && (
              <div className="parameter-item">
                <span className="parameter-label">Training Data</span>
                <span className="parameter-value">
                  {parameters.trainingData}
                </span>
              </div>
            )}
            {modalities.length > 0 && (
              <div className="parameter-item">
                <span className="parameter-label">Supported Modalities</span>
                <div className="modality-tags">
                  {modalities.map((m, i) => (
                    <span key={i} className="modality-tag" data-modality={m}>
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="parameter-item">
              <span className="parameter-label">Tool Support</span>
              <span className="parameter-value">
                {parameters.toolUse ? '✅ Enabled' : '❌ Disabled'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────── */
/* Main selector component                    */
/* ────────────────────────────────────────── */
const ModelSelector = ({
  selectedModel,
  setSelectedModel,
  isModelModalOpen,
  setIsModelModalOpen,
  techSpecsStorageKey,
  legacyStorageKey,
  maxItems,
}) => {
  /* responsive truncation */
  const isSmallScreen = useIsSmallScreen();
  const effectiveMax = maxItems !== undefined ? maxItems : (isSmallScreen ? 2 : undefined);

  /* internal state */
  const [searchQuery, setSearchQuery] = React.useState('');

  const [showLegacy, setShowLegacy] = React.useState(() => {
    if (!legacyStorageKey) return false;
    try {
      if (typeof window !== 'undefined') {
        return localStorage.getItem(legacyStorageKey) === 'true';
      }
    } catch (e) {
      console.warn('localStorage access failed:', e);
    }
    return false;
  });

  const [showTechnicalSpecs, setShowTechnicalSpecs] = React.useState(() => {
    if (!techSpecsStorageKey) return false;
    // Safe access to localStorage
    try {
      if (typeof window !== 'undefined') {
        return localStorage.getItem(techSpecsStorageKey) === 'true';
      }
    } catch (e) {
      console.warn('localStorage access failed:', e);
    }
    return false;
  });

  const searchRef = React.useRef(null);

  /* Focus search when modal opens & reset query on close (optional) */
  React.useEffect(() => {
    if (isModelModalOpen) {
      // Slight delay to ensure modal DOM is ready
      const timer = setTimeout(() => {
        searchRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setSearchQuery('');
    }
  }, [isModelModalOpen]);

  /* Filter logic */
  const filteredModels = React.useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase();

    return Object.keys(modelInfo).filter((id) => {
      const model = modelInfo[id];

      // 1. Legacy Check
      if (model.legacy && !showLegacy) {
        return false;
      }

      // 2. Search Query Check
      if (!lowerQuery) return true; // show all (respecting legacy) if no query

      return (
        model.name.toLowerCase().includes(lowerQuery) ||
        model.description.toLowerCase().includes(lowerQuery) ||
        (model.strengths && model.strengths.some((s) => s.toLowerCase().includes(lowerQuery)))
      );
    });
  }, [searchQuery, showLegacy]);

  /* Persist preferences */
  const handleLegacyToggle = (val) => {
    setShowLegacy(val);
    if (legacyStorageKey && typeof window !== 'undefined') {
      localStorage.setItem(legacyStorageKey, val.toString());
    }
  };

  const handleTechSpecsToggle = (val) => {
    setShowTechnicalSpecs(val);
    if (techSpecsStorageKey && typeof window !== 'undefined') {
      localStorage.setItem(techSpecsStorageKey, val.toString());
    }
  };

  return (
    <div className="model-selector">
      {/* ─── Trigger chip ─────────────────── */}
      <Tooltip label="Tap to change AI model" align="bottom">
        <Button
          kind="ghost"
          size="sm"
          className="model-trigger"
          onClick={() => setIsModelModalOpen(true)}
          aria-label="Change AI model"
          renderIcon={ChevronDown}
          iconDescription="open model list"
        >
          <div className="current-model">
            <strong>{modelInfo[selectedModel]?.name || selectedModel}</strong>
            <span className="model-description">
              {modelInfo[selectedModel]?.description}
            </span>
          </div>
        </Button>
      </Tooltip>

      {/* ─── Modal list ───────────────────── */}
      <Modal
        open={isModelModalOpen}
        passiveModal
        size="lg"
        modalHeading="Select AI Model"
        onRequestClose={() => setIsModelModalOpen(false)}
        className="model-selector-modal"
      >
        {/* search + toggle row */}
        <div className="model-modal-controls" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <Search
              ref={searchRef}
              placeholder="Search models…"
              labelText="Search models"
              id="model-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="model-search"
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Toggle
              id={`${legacyStorageKey || 'legacy'}-toggle`}
              labelText="Show older models"
              labelA="Hidden"
              labelB="Visible"
              toggled={showLegacy}
              onToggle={handleLegacyToggle}
              className="legacy-toggle"
              size="sm"
            />
            <Toggle
              id={`${techSpecsStorageKey || 'specs'}-toggle`}
              labelText="Show technical specs"
              toggled={showTechnicalSpecs}
              onToggle={handleTechSpecsToggle}
              className="specs-toggle"
              size="sm"
            />
          </div>
        </div>

        {/* grid of cards */}
        <div className="model-modal-grid">
          {filteredModels.length > 0 ? (
            filteredModels.map((id) => (
              <ModelCard
                key={id}
                modelId={id}
                selected={id === selectedModel}
                onSelect={(m) => {
                  setSelectedModel(m);
                  setIsModelModalOpen(false);
                }}
                showTechnicalSpecs={showTechnicalSpecs}
                maxItems={effectiveMax}
              />
            ))
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--cds-text-secondary)' }}>
              No models found. Try adjusting your search or showing older models.
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export { ModelSelector, ModelCard };